import { useState } from 'react';
import { Building2Icon, ChevronsUpDownIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { SimpleAccount } from '@/lib/types';
import { AccountCommand } from './AccountCommand';

export interface AccountSwitcherProps {
  accounts: SimpleAccount[];
  currentAccountCode: string;
  favoriteAccountIds: string[];
  /** 別のアカウントを選んだときだけ呼ぶ。 */
  onSelect: (code: string) => void;
  onToggleFavorite?: (accountId: string, shouldBeFavorite: boolean) => void;
}

/** ダッシュボード表示中に、ツールバーからアカウントを検索して切り替える。 */
export function AccountSwitcher({
  accounts,
  currentAccountCode,
  favoriteAccountIds,
  onSelect,
  onToggleFavorite,
}: AccountSwitcherProps) {
  const [open, setOpen] = useState(false);
  const currentName =
    accounts.find((account) => account.code === currentAccountCode)?.name ?? currentAccountCode;

  const handleSelect = (code: string) => {
    setOpen(false);
    if (code !== currentAccountCode) onSelect(code);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={`アカウントを切り替える（表示中: ${currentName}）`}
          className="h-9 max-w-[340px] gap-2 px-2.5 data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/20"
        >
          <Building2Icon aria-hidden className="text-muted-foreground" />
          <span className="min-w-0 truncate">{currentName}</span>
          <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs font-normal text-muted-foreground">
            {currentAccountCode}
          </span>
          <ChevronsUpDownIcon aria-hidden className="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[440px] max-w-[calc(100vw-2rem)] overflow-hidden p-0"
      >
        <AccountCommand
          accounts={accounts}
          favoriteAccountIds={favoriteAccountIds}
          currentAccountCode={currentAccountCode}
          onSelect={handleSelect}
          onToggleFavorite={onToggleFavorite}
          selectLabel="切り替え"
          autoFocus
          listClassName="h-[320px]"
        />
      </PopoverContent>
    </Popover>
  );
}
